import React, { type ChangeEvent, type FC, useState } from 'react';
import clsx from 'clsx';
import {
  type Control,
  type FieldValues,
  type UseFormSetError,
  Controller,
  useFormContext,
} from 'react-hook-form';
import { CheckIcon } from 'assets/images';
import ValidationError from 'components/ValidationError';
import styles from './Checkbox.module.scss';

interface ICheckboxProps extends React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> {
  name: string;
  control: Control<FieldValues, object>;
  clearErrors: (name: string) => void;
  setError: UseFormSetError<FieldValues>;
  label?: string | React.ReactElement;
  isLoading?: boolean;
  required: boolean;
}

const Checkbox: FC<ICheckboxProps> = (props) => {
  const {
    control,
    clearErrors,
    setError,
    name,
    label,
    className,
    isLoading = false,
    required,
  } = props;

  const [previousError, setPreviousError] = useState<string>('');

  const handleChange = (event: ChangeEvent<HTMLInputElement>, isSubmitted: boolean) => {
    if (isSubmitted) {
      if (!event.target.value || event.target.value === 'false') {
        clearErrors(name);

        return;
      }

      if (event.target.value === 'true') {
        setError(name, { message: previousError });

        return;
      }
    }
  };

  const validationErrorId = `${name}-error`;

  const { trigger } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error }, formState: { isSubmitted } }) => (
        <div className={styles.wrapper}>
          <label className={clsx(styles.label, className)}>
            <input
              className={styles.input}
              type="checkbox"
              aria-invalid={!!error}
              aria-label={typeof label === 'string' ? label : undefined}
              aria-describedby={error?.message ? validationErrorId : undefined}
              {...field}
              checked={!!field.value}
              onChange={(event) => {
                if (isLoading) {
                  event.preventDefault();
                } else {
                  field.onChange(event);
                  handleChange(event, isSubmitted);
                }

                trigger(name);
              }}
              required={required}
            />

            <div
              className={clsx(styles.checkbox, field.value && styles.checkboxActive)}
              role="presentation"
              aria-hidden="true"
            >
              <CheckIcon className={styles.icon} role="presentation" />
            </div>

            <span>{label}</span>
          </label>
          {!!error?.message && (
            <ValidationError
              setPreviousError={setPreviousError}
              message={error.message}
              className={styles.error}
              id={validationErrorId}
              role="status"
              aria-live="polite"
            />
          )}
        </div>
      )}
    />
  );
};

export default Checkbox;
